/**
 * The latest energetic summary as 18 horizontal bars, one per check-in signal in a fixed order:
 * bar length is the 0-10 value and its opacity the confidence, so a confident reading reads as a
 * solid bar and a tentative one as a faint bar. Signals the conversation said nothing about stay
 * empty and are labelled "not inferred" for screen readers. Drawn as SVG with presentation
 * attributes only (no inline styles), so it scales with the dialog and respects the CSP.
 *
 * @module apps/web/src/components/flux-agent/learning/FluxAgentSignalSummaryBars
 */

import type { FluxAgentSignalSummaryPayload } from '@/lib/fluxAgentLearningApi';

/** The 18 signals in the server's order with their user-facing labels (`EnergySignal::label`). */
export const SIGNAL_LABELS: [string, string][] = [
  ['valence', 'Pleasantness'],
  ['activation', 'Alertness'],
  ['positive_affect', 'Positive feelings'],
  ['negative_affect', 'Negative feelings'],
  ['autonomy', 'Choice'],
  ['competence', 'Feeling capable'],
  ['relatedness', 'Connection'],
  ['vigor', 'Vigor'],
  ['dedication', 'Sense of purpose'],
  ['absorption', 'Absorption'],
  ['mental_demand', 'Mental demand'],
  ['physical_demand', 'Physical demand'],
  ['temporal_demand', 'Time pressure'],
  ['effort', 'Effort'],
  ['frustration', 'Frustration'],
  ['perceived_success', 'Expected success'],
  ['start_resistance', 'Starting friction'],
  ['restorative_pull', 'Desire to restore'],
];

/** Display words for the tone labels. */
const TONE_WORDS: Record<FluxAgentSignalSummaryPayload['toneLabel'], string> = {
  neutral: 'Neutral',
  calm: 'Calm',
  upbeat: 'Upbeat',
  excited: 'Excited',
  tired: 'Tired',
  frustrated: 'Frustrated',
  overwhelmed: 'Overwhelmed',
  low: 'Low',
};

/** Row height in SVG units. */
const ROW = 18;
/** Width reserved for labels in SVG units. */
const LABEL = 130;
/** Width of a full (10/10) bar in SVG units. */
const BAR = 200;

/** Inputs for {@link FluxAgentSignalSummaryBars}. */
export interface FluxAgentSignalSummaryBarsProps {
  /** The summary to draw. */
  summary: FluxAgentSignalSummaryPayload;
}

/** Draw one energetic summary as 18 confidence-shaded bars with its tone and time. */
export function FluxAgentSignalSummaryBars({ summary }: FluxAgentSignalSummaryBarsProps) {
  const readings = new Map(
    summary.signals.map(([signal, value, confidence]) => [signal, { value, confidence }])
  );
  const when = new Date(summary.at);
  const time = Number.isNaN(when.getTime())
    ? summary.at
    : when.toLocaleString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
  const height = ROW * SIGNAL_LABELS.length;
  return (
    <figure className="space-y-2 rounded-md border px-3 py-2">
      <figcaption className="text-sm">
        <span className="font-medium">{TONE_WORDS[summary.toneLabel] ?? 'Neutral'}</span>
        <span className="text-muted-foreground"> · {time}</span>
        {summary.basis && (
          <span className="text-muted-foreground block text-xs">{summary.basis}</span>
        )}
      </figcaption>
      <svg
        viewBox={`0 0 ${LABEL + BAR + 40} ${height}`}
        className="text-primary h-auto w-full"
        role="img"
        aria-label={`Energetic summary: ${SIGNAL_LABELS.map(([key, label]) => {
          const reading = readings.get(key);
          return reading
            ? `${label} ${Math.round(reading.value)} of 10, ${Math.round(reading.confidence * 100)}% confident`
            : `${label} not inferred`;
        }).join('; ')}`}
      >
        {SIGNAL_LABELS.map(([key, label], index) => {
          const reading = readings.get(key);
          const y = index * ROW;
          return (
            <g key={key}>
              <text
                x={LABEL - 6}
                y={y + ROW * 0.7}
                textAnchor="end"
                fontSize="11"
                fill="currentColor"
                className="text-muted-foreground"
              >
                {label}
              </text>
              <rect
                x={LABEL}
                y={y + 3}
                width={BAR}
                height={ROW - 6}
                rx="2"
                fill="currentColor"
                fillOpacity="0.08"
              />
              {reading && (
                <rect
                  x={LABEL}
                  y={y + 3}
                  width={(BAR * Math.min(10, Math.max(0, reading.value))) / 10}
                  height={ROW - 6}
                  rx="2"
                  fill="currentColor"
                  fillOpacity={0.25 + 0.75 * Math.min(1, Math.max(0, reading.confidence))}
                />
              )}
              <text
                x={LABEL + BAR + 6}
                y={y + ROW * 0.7}
                fontSize="10"
                fill="currentColor"
                className="text-muted-foreground"
              >
                {reading ? Math.round(reading.value) : '–'}
              </text>
            </g>
          );
        })}
      </svg>
      <p className="text-muted-foreground text-xs">
        Bar length is the estimate out of 10; fainter bars are less certain. Empty rows were not
        inferred.
      </p>
    </figure>
  );
}
